import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { RefreshCw, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { getIdToken, useAuth } from '@pooflabs/web';
import { CopyableMono } from './CopyableMono';
import { formatExpiry } from './CountdownTimer';
import { setPaymentDeposits } from '@/lib/collections/paymentDeposits';
import {
  getUserPaymentAccounts,
  runGetSolBalanceQueryForUserPaymentAccounts,
  runGetUsdcBalanceQueryForUserPaymentAccounts,
  runGetUsdtBalanceQueryForUserPaymentAccounts,
  setUserPaymentAccounts,
} from '@/lib/collections/userPaymentAccounts';
import { setBalanceApplicationRequests } from '@/lib/collections/balanceApplicationRequests';
import {
  runSolBalanceQueryForCommonQueries,
  runTokenBalanceQueryForCommonQueries,
  runUsdcBalanceQueryForCommonQueries,
} from '@/lib/collections/commonQueries';
import { Address, Time } from '@/lib/db-client';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { USDT_MINT } from '@/lib/constants';
import { checkoutConnectKey, clearPoofSession } from '@/lib/auth';
import { toast } from 'sonner';

const RAIL_LABEL: Record<string, string> = { usdc: 'USDC', sol: 'SOL', usdt: 'USDT' };

// CANONICAL UNITS: nativeAmount is ALWAYS a string of raw on-chain base units (no decimals).
// USDC/USDT: 6 decimals → "29000000" = 29 USDC
// SOL: 9 decimals (lamports) → "100000000" = 0.1 SOL
const RAIL_DECIMALS: Record<string, number> = { sol: 9, usdc: 6, usdt: 6 };
const COMMON_BALANCE_QUERY_ID = 'checkout-wallet-balance';
const SOL_FEE_BUFFER_RAW = 5_000_000; // keep a tiny cushion for network fees when paying with SOL.
const PAYMENT_SUBMIT_TIMEOUT_MS = 45_000;

/**
 * Convert raw base-unit string to a human-readable decimal string.
 * e.g. rawToHuman("29000000", "usdc") → "29"
 *      rawToHuman("100000000", "sol") → "0.1"
 * Used for display, Solana Pay URI `amount=` param, and button labels.
 */
function rawToHuman(raw: string | undefined, rail: string): string {
  if (!raw) return '';
  const dec = RAIL_DECIMALS[rail] ?? 6;
  const n = Number(raw) / 10 ** dec;
  if (!isFinite(n)) return '';
  // Trim trailing zeros, cap at `dec` fractional digits
  return n.toLocaleString('en-US', { maximumFractionDigits: dec, useGrouping: false });
}

function formatRawAmount(raw: number, rail: string): string {
  return rawToHuman(String(Math.max(0, raw)), rail) || '0';
}

function requiredWalletAmount(deficitRaw: number, rail: string): number {
  return deficitRaw + (rail === 'sol' ? SOL_FEE_BUFFER_RAW : 0);
}

function balanceShortfallMessage(balanceRaw: number, neededRaw: number, rail: string, label: string): string {
  const feeCopy = rail === 'sol' ? ' plus a small fee cushion' : '';
  return `Insufficient ${label}. Wallet has ${formatRawAmount(balanceRaw, rail)} ${label}; needs ${formatRawAmount(neededRaw, rail)} ${label}${feeCopy}.`;
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(id);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

async function queryWalletBalance(walletAddress: string, rail: string): Promise<number> {
  if (rail === 'sol') {
    return Number(await runSolBalanceQueryForCommonQueries(COMMON_BALANCE_QUERY_ID, { walletAddress }));
  }
  if (rail === 'usdc') {
    return Number(await runUsdcBalanceQueryForCommonQueries(COMMON_BALANCE_QUERY_ID, { walletAddress }));
  }
  if (rail === 'usdt') {
    return Number(await runTokenBalanceQueryForCommonQueries(COMMON_BALANCE_QUERY_ID, {
      walletAddress,
      tokenMint: USDT_MINT,
    }));
  }
  return 0;
}

async function queryUserBalance(userAccountId: string, rail: string): Promise<number> {
  if (rail === 'sol') return Number(await runGetSolBalanceQueryForUserPaymentAccounts(userAccountId));
  if (rail === 'usdc') return Number(await runGetUsdcBalanceQueryForUserPaymentAccounts(userAccountId));
  if (rail === 'usdt') return Number(await runGetUsdtBalanceQueryForUserPaymentAccounts(userAccountId));
  return 0;
}

async function queryStoredBalanceForWallet(walletAddress: string, rail: string): Promise<number> {
  const userAccountId = await deriveUserAccountId(walletAddress);
  const existingAccount = await getUserPaymentAccounts(userAccountId);
  if (!existingAccount || existingAccount.user !== walletAddress) return 0;
  return queryUserBalance(userAccountId, rail);
}

function randomDepositId(): string {
  const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return 'dep_' + Array.from(arr).map((b) => chars[b % 58]).join('');
}

function randomRequestId(): string {
  const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return 'bar_' + Array.from(arr).map((b) => chars[b % 58]).join('');
}

/**
 * Derive a deterministic userAccountId from a wallet address.
 * Uses SHA-256 of the wallet address, then takes the first 24 hex chars prefixed with "ua_".
 * This is consistent with the backend derivePdaAccountId pattern but keyed to wallet (not appid+tenantUserId).
 */
async function deriveUserAccountId(walletAddress: string): Promise<string> {
  const enc = new TextEncoder();
  const hashBuf = await crypto.subtle.digest('SHA-256', enc.encode(`wallet:${walletAddress}`));
  const hex = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `ua_${hex.slice(0, 24)}`;
}

function isValidBase58Address(addr: string | null | undefined): addr is string {
  if (!addr) return false;
  if (addr.startsWith('pp_') || addr.startsWith('ua_')) return false;
  // Simple sanity check: base58 chars only, 32–44 chars
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(addr);
}

interface PublicSession {
  sessionId: string;
  appid: string;
  tenantName?: string;
  amountUsdCents: number;
  solanaPayReference?: string;
  nativeAmount?: string;
  // pdaAddress is the user's resolved PDA base58 address (for QR code display)
  pdaAddress?: string | null;
  paymentAddress?: string | null;
  status: string;
  expiresAt: number;
}

interface CryptoRailPanelProps {
  session: PublicSession;
  rail: string;
  onRefresh?: () => void | Promise<void>;
  isQuoteLoading?: boolean;
  onConfirmPayment?: () => void;
  confirmingPayment?: boolean;
}

export const CryptoRailPanel: React.FC<CryptoRailPanelProps> = ({
  session,
  rail,
  onRefresh,
  isQuoteLoading = false,
  onConfirmPayment,
  confirmingPayment = false,
}) => {
  const { user, login, logout } = useAuth();
  const [paying, setPaying] = useState(false);
  const [payStep, setPayStep] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [pendingConnect, setPendingConnect] = useState(false);
  const [walletBalance, setWalletBalance] = useState<{ address: string; rail: string; raw: number } | null>(null);
  const [storedBalance, setStoredBalance] = useState<{ address: string; rail: string; raw: number } | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const {
    sessionId, appid, tenantName, amountUsdCents,
    pdaAddress, paymentAddress, solanaPayReference, nativeAmount, expiresAt,
  } = session;

  // Per-session connect marker: a payer must explicitly connect for THIS
  // checkout even if a global session is already persisted, so a login from
  // another app's checkout (same pay.poofy.ai origin) does not bleed over.
  const [sessionConnectAddr, setSessionConnectAddr] = useState<string | null>(() => {
    try { return sessionStorage.getItem(checkoutConnectKey(sessionId)); } catch { return null; }
  });
  const connectedForSession = !!user?.address && sessionConnectAddr === user.address;

  const usdStr = (amountUsdCents / 100).toFixed(2);
  const label = RAIL_LABEL[rail] ?? rail.toUpperCase();
  const resolvedPaymentAddress = pdaAddress ?? paymentAddress;
  const addressReady = isValidBase58Address(resolvedPaymentAddress);

  // Human-readable amount for display, button labels, and Solana Pay URI.
  // Solana Pay spec requires human-decimal for the `amount=` param — NOT raw base units.
  const humanAmount = rawToHuman(nativeAmount, rail);
  const quoteReady = !isQuoteLoading && !!humanAmount;
  const paymentDetected = session.status === 'payment_detected';
  const rawAmount = nativeAmount ? Number(nativeAmount) : 0;
  const activeWalletBalance =
    walletBalance?.address === user?.address && walletBalance?.rail === rail ? walletBalance.raw : null;
  const activeStoredBalance =
    storedBalance?.address === user?.address && storedBalance?.rail === rail ? storedBalance.raw : 0;
  const remainingWalletNeeded = Math.max(0, rawAmount - activeStoredBalance);
  const knownWalletDeficit = !!user?.address && rawAmount > 0 && activeWalletBalance != null
    && remainingWalletNeeded > 0 && activeWalletBalance < requiredWalletAmount(remainingWalletNeeded, rail);

  const refreshWalletBalance = useCallback(async () => {
    if (!user?.address || !rawAmount || isQuoteLoading) {
      setWalletBalance(null);
      setStoredBalance(null);
      setBalanceError(null);
      setBalanceLoading(false);
      return;
    }

    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const raw = await queryWalletBalance(user.address, rail);
      setWalletBalance({ address: user.address, rail, raw });
      const storedRaw = await queryStoredBalanceForWallet(user.address, rail).catch(() => 0);
      setStoredBalance({ address: user.address, rail, raw: storedRaw });
    } catch (e) {
      console.error('Failed to check wallet balance', e);
      setWalletBalance(null);
      setStoredBalance(null);
      setBalanceError('Could not check wallet balance.');
    } finally {
      setBalanceLoading(false);
    }
  }, [isQuoteLoading, rail, rawAmount, user?.address]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user?.address || !rawAmount || isQuoteLoading) {
        if (!cancelled) {
          setWalletBalance(null);
          setStoredBalance(null);
          setBalanceError(null);
          setBalanceLoading(false);
        }
        return;
      }

      setBalanceLoading(true);
      setBalanceError(null);
      try {
        const raw = await queryWalletBalance(user.address, rail);
        const storedRaw = await queryStoredBalanceForWallet(user.address, rail).catch(() => 0);
        if (!cancelled) {
          setWalletBalance({ address: user.address, rail, raw });
          setStoredBalance({ address: user.address, rail, raw: storedRaw });
        }
      } catch (e) {
        console.error('Failed to check wallet balance', e);
        if (!cancelled) {
          setWalletBalance(null);
          setStoredBalance(null);
          setBalanceError('Could not check wallet balance.');
        }
      } finally {
        if (!cancelled) setBalanceLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [isQuoteLoading, rail, rawAmount, user?.address]);

  // Once the payer has explicitly connected for this session, persist the
  // marker against the live wallet address (login resolves the address async,
  // so we wait for it rather than reading it right after login()).
  useEffect(() => {
    if (!pendingConnect || !user?.address) return;
    try { sessionStorage.setItem(checkoutConnectKey(sessionId), user.address); } catch { /* storage unavailable */ }
    setSessionConnectAddr(user.address);
    setPendingConnect(false);
  }, [pendingConnect, user?.address, sessionId]);

  // Build Solana Pay URI only if address is a valid base58 public key.
  // `amount` must be the human-decimal string (Solana Pay spec).
  const solanaPayUri = quoteReady && addressReady
    ? `solana:${resolvedPaymentAddress}?${new URLSearchParams({
        amount: humanAmount,
        ...(solanaPayReference ? { reference: solanaPayReference } : {}),
        ...(tenantName ? { label: tenantName } : {}),
        message: `Top up for session ${sessionId}`,
      }).toString()}`
    : '';

  const handleWalletPay = async () => {
    if (!user?.address) return;
    if (isQuoteLoading) {
      toast.error('Quote is still updating');
      return;
    }
    if (!rawAmount) {
      toast.error('Amount not set — wait for price refresh');
      return;
    }

    setPaying(true);
    setPayStep('Checking wallet balance…');
    try {
      const userAccountId = await deriveUserAccountId(user.address);

      const existingAccount = await getUserPaymentAccounts(userAccountId);
      if (existingAccount && existingAccount.user !== user.address) {
        throw new Error('Balance account belongs to another wallet');
      }

      const currentPdaBalance = existingAccount ? await queryUserBalance(userAccountId, rail).catch(() => 0) : 0;
      const depositDeficit = Math.max(0, rawAmount - currentPdaBalance);
      if (depositDeficit > 0) {
        const directWalletBalance = await queryWalletBalance(user.address, rail);
        setWalletBalance({ address: user.address, rail, raw: directWalletBalance });

        const requiredRaw = requiredWalletAmount(depositDeficit, rail);
        if (directWalletBalance < requiredRaw) {
          throw new Error(balanceShortfallMessage(directWalletBalance, requiredRaw, rail, label));
        }
      }

      setPayStep('Preparing wallet payment…');
      if (!existingAccount) {
        const accountCreated = await setUserPaymentAccounts(userAccountId, {
          user: Address.publicKey(user.address),
          createdAt: Time.Now,
        });
        if (!accountCreated) {
          const accountAfterRetry = await getUserPaymentAccounts(userAccountId);
          if (!accountAfterRetry || accountAfterRetry.user !== user.address) {
            throw new Error('Could not prepare balance account');
          }
        }
      }

      if (depositDeficit > 0) {
        setPayStep('Submitting wallet payment…');
        const depositId = randomDepositId();
        let success = false;
        try {
          success = await withTimeout(
            setPaymentDeposits(depositId, {
              payer: Address.publicKey(user.address),
              userAccountId,
              amt: depositDeficit,
              rail,
              ts: Time.Now,
            }),
            PAYMENT_SUBMIT_TIMEOUT_MS,
            'Wallet payment is taking longer than expected. If your wallet shows it completed, wait a moment and refresh status.',
          );
        } catch (e) {
          const balanceAfterTimeout = await queryUserBalance(userAccountId, rail).catch(() => 0);
          if (balanceAfterTimeout >= rawAmount) {
            success = true;
          } else {
            throw e;
          }
        }
        if (!success) {
          throw new Error('Payment was denied — check your wallet balance');
        }
      }

      setPayStep('Confirming checkout…');
      const requestId = randomRequestId();
      const requestCreated = await setBalanceApplicationRequests(requestId, {
        sessionId,
        appid,
        payer: Address.publicKey(user.address),
        userAccountId,
        rail,
        amount: rawAmount,
        status: 'pending',
        createdAt: Time.Now,
      });
      if (!requestCreated) {
        throw new Error('Payment was sent, but checkout could not be confirmed yet. Refresh status and try again.');
      }

      const token = await getIdToken();
      if (!token) {
        throw new Error('Connect wallet to finish checkout');
      }
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.post(`/api/v1/sessions/${sessionId}/apply-balance`, {
        requestId,
      });
      toast.success('Payment submitted — checkout is settling');
      await refreshWalletBalance();
      await onRefresh?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Payment failed');
    } finally {
      setPaying(false);
      setPayStep(null);
    }
  };

  const handleConnectForSession = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      // Only run the wallet handshake when there is no live session. When a
      // session already exists (e.g. bled over from another app's checkout on
      // this shared origin), the explicit click is itself the per-session
      // consent — we just mark THIS checkout, and the connected state then
      // reveals the address so a payer can "Switch wallet" if it is not theirs.
      if (!user?.address) await login();
      setPendingConnect(true);
    } catch (e: any) {
      if (e?.message) toast.error(e.message);
    } finally {
      setConnecting(false);
    }
  };

  // "Not you?" — drop the persisted session (defeats a ghost/mismatched wallet)
  // and start a fresh wallet selection for this checkout.
  const handleSwitchWallet = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      try { sessionStorage.removeItem(checkoutConnectKey(sessionId)); } catch { /* storage unavailable */ }
      setSessionConnectAddr(null);
      await clearPoofSession(logout);
      await login();
      setPendingConnect(true);
    } catch (e: any) {
      if (e?.message) toast.error(e.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    setRefreshNote(null);
    try {
      await onRefresh();
      setRefreshNote('Checked just now');
      toast.success('Status refreshed');
    } catch (e: any) {
      setRefreshNote('Could not refresh status');
      toast.error(e?.message ?? 'Could not refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className='flex flex-col gap-6'>

      {/* ── Section 1: Manual send ─────────────────────────────────── */}
      <div className='flex flex-col gap-4'>
        <div>
          <div className='text-sm font-semibold' style={{ color: 'var(--pp-text)' }}>
            Send from another wallet
          </div>
          <div className='text-sm mt-1' style={{ color: 'var(--pp-text-muted)' }}>
            {isQuoteLoading ? (
              <span className='inline-flex items-center gap-2'>
                <Loader2 size={15} className='animate-spin' />
                Updating {label} quote
              </span>
            ) : (
              <>Send{humanAmount ? ` ${humanAmount} ${label}` : ''} to this address</>
            )}
          </div>
        </div>

        {addressReady ? (
          <div
            className='rounded-lg border p-3 flex flex-col gap-2'
            style={{ background: 'var(--pp-subtle)', borderColor: 'var(--pp-border)' }}
          >
            <div className='flex items-center justify-between gap-2'>
              <CopyableMono value={resolvedPaymentAddress as string} truncate={false} className='min-w-0 break-all' />
            </div>
            <p className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
              <AlertTriangle size={11} className='inline mr-1' style={{ color: 'var(--pp-warning)' }} />
              {isQuoteLoading
                ? 'Wait for the updated quote before sending.'
                : `Only send ${label} on Solana. Slightly more is okay; less will not settle.`}
            </p>
          </div>
        ) : (
          <div
            className='rounded-lg border p-3 flex items-center gap-2'
            style={{ background: 'var(--pp-subtle)', borderColor: 'var(--pp-border)', opacity: 0.6 }}
          >
            <span className='text-sm' style={{ color: 'var(--pp-text-subtle)', fontFamily: 'var(--pp-mono)' }}>
              Preparing deposit address…
            </span>
          </div>
        )}

        {/* QR code */}
        <div className='flex flex-col items-center gap-2'>
          <div className='text-xs font-medium uppercase tracking-wide' style={{ color: 'var(--pp-text-subtle)' }}>
            Scan QR instead
          </div>
          {quoteReady && addressReady && solanaPayUri ? (
            <div
              className='p-4 rounded-lg border inline-block'
              style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
            >
              <QRCodeSVG value={solanaPayUri} size={176} level='M' />
            </div>
          ) : (
            <div
              className='w-44 h-44 rounded-lg border flex flex-col items-center justify-center gap-2'
              style={{ background: 'var(--pp-subtle)', borderColor: 'var(--pp-border)' }}
            >
              <Loader2 size={20} className={isQuoteLoading ? 'animate-spin' : ''} />
              <span className='text-xs text-center px-3' style={{ color: 'var(--pp-text-subtle)' }}>
                {isQuoteLoading ? 'Updating payment amount...' : 'Preparing deposit address…'}
              </span>
            </div>
          )}
        </div>

        {/* Amount display — human-readable */}
        <div className='text-center'>
          <div
            className='text-4xl font-semibold tabular-nums'
            style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text)', letterSpacing: '-0.03em' }}
          >
            {isQuoteLoading ? (
              <span className='inline-flex items-center gap-3 text-2xl'>
                <Loader2 size={22} className='animate-spin' />
                Updating quote
              </span>
            ) : (
              <>{humanAmount || '—'} {label}</>
            )}
          </div>
          <div className='text-sm mt-0.5' style={{ color: 'var(--pp-text-subtle)' }}>
            ${usdStr} USD
          </div>
        </div>
      </div>

      {paymentDetected ? (
        <div
          className='rounded-lg border p-4'
          style={{ background: 'rgba(34,197,94,0.10)', borderColor: 'rgba(34,197,94,0.45)' }}
        >
          <div className='flex items-start gap-3'>
            <CheckCircle2 size={20} className='mt-0.5' style={{ color: '#34d399' }} />
            <div className='min-w-0 flex-1'>
              <h3 className='font-semibold' style={{ color: 'var(--pp-text)' }}>Funds detected</h3>
              <p className='mt-1 text-sm' style={{ color: 'var(--pp-text-muted)' }}>
                We found funds at the deposit address. Finish checkout when you are ready.
              </p>
              <button
                className='mt-4 py-2.5 px-4 rounded text-sm font-semibold text-center text-white inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'
                style={{ background: 'var(--pp-action)' }}
                onClick={onConfirmPayment}
                disabled={!onConfirmPayment || confirmingPayment}
              >
                {confirmingPayment ? <Loader2 size={16} className='animate-spin' /> : <CheckCircle2 size={16} />}
                Finish checkout
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ── Divider ───────────────────────────────────────────────── */}
          <div className='flex items-center gap-3'>
            <div className='flex-1 h-px' style={{ background: 'var(--pp-border)' }} />
            <span className='text-xs font-semibold tracking-wide' style={{ color: 'var(--pp-text-subtle)' }}>
              OR
            </span>
            <div className='flex-1 h-px' style={{ background: 'var(--pp-border)' }} />
          </div>

          {/* ── Section 2: Direct wallet pay ──────────────────────────── */}
          <div className='flex flex-col gap-2'>
            {!connectedForSession ? (
              <>
                <button
                  data-testid='checkout-connect-pay'
                  onClick={handleConnectForSession}
                  disabled={connecting}
                  className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2'
                  style={{ background: 'var(--pp-action)' }}
                >
                  {connecting ? <><Loader2 size={16} className='animate-spin' />Connecting…</> : 'Connect wallet & pay'}
                </button>
                <p className='text-center text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
                  Connect your wallet to pay for this checkout.
                </p>
              </>
            ) : (
              <>
                <button
                  data-testid='checkout-direct-pay'
                  onClick={handleWalletPay}
                  disabled={paying || !nativeAmount || isQuoteLoading || balanceLoading || knownWalletDeficit}
                  className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2'
                  style={{ background: 'var(--pp-action)' }}
                >
                  {isQuoteLoading
                    ? 'Updating quote...'
                    : balanceLoading
                      ? <><Loader2 size={16} className='animate-spin' />Checking balance…</>
                      : knownWalletDeficit
                        ? `Insufficient ${label} balance`
                        : paying
                          ? <><Loader2 size={16} className='animate-spin' />{payStep ?? 'Paying…'}</>
                          : `Pay ${humanAmount ? `${humanAmount} ` : ''}${label}`}
                </button>
                <p className='text-center text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
                  Connected: {user?.address.slice(0, 4)}…{user?.address.slice(-4)}
                </p>
                {activeWalletBalance != null && (
                  <p
                    className='text-center text-xs'
                    style={{ color: knownWalletDeficit ? 'var(--pp-danger)' : 'var(--pp-text-subtle)' }}
                  >
                    Wallet balance: {formatRawAmount(activeWalletBalance, rail)} {label}
                  </p>
                )}
                {activeStoredBalance > 0 && (
                  <p className='text-center text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
                    Saved balance: {formatRawAmount(activeStoredBalance, rail)} {label}
                  </p>
                )}
                {balanceError && (
                  <button
                    onClick={refreshWalletBalance}
                    className='text-center text-xs underline underline-offset-2'
                    style={{ color: 'var(--pp-warning)' }}
                  >
                    {balanceError} Retry
                  </button>
                )}
                <button
                  data-testid='checkout-switch-wallet'
                  onClick={handleSwitchWallet}
                  disabled={connecting}
                  className='text-center text-xs underline underline-offset-2 disabled:opacity-50'
                  style={{ color: 'var(--pp-text-muted)' }}
                >
                  {connecting ? 'Switching…' : 'Not you? Switch wallet'}
                </button>
              </>
            )}

            {onRefresh && (
              <div className='flex flex-col gap-1'>
                <button
                  onClick={handleRefreshStatus}
                  disabled={refreshing}
                  className='w-full py-2 px-4 rounded text-xs font-medium text-center border flex items-center justify-center gap-1.5 transition-colors hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed'
                  style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)' }}
                >
                  <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                  {refreshing ? 'Checking status…' : 'Refresh status'}
                </button>
                {refreshNote && (
                  <p className='text-center text-[11px]' style={{ color: 'var(--pp-text-subtle)' }}>
                    {refreshNote}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Footer info ───────────────────────────────────────────── */}
      <div
        className='text-xs grid grid-cols-3 gap-2 pt-2 border-t'
        style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-subtle)' }}
      >
        <div className='flex flex-col gap-0.5'>
          <span className='font-medium uppercase tracking-wide text-[10px]'>Session</span>
          <span style={{ fontFamily: 'var(--pp-mono)' }}>{sessionId.slice(0, 12)}…</span>
        </div>
        {solanaPayReference && (
          <div className='flex flex-col gap-0.5'>
            <span className='font-medium uppercase tracking-wide text-[10px]'>Reference</span>
            <span style={{ fontFamily: 'var(--pp-mono)' }}>{solanaPayReference.slice(0, 8)}…</span>
          </div>
        )}
        <div className='flex flex-col gap-0.5'>
          <span className='font-medium uppercase tracking-wide text-[10px]'>Expires</span>
          <span style={{ fontFamily: 'var(--pp-mono)' }}>
            {formatExpiry(expiresAt)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CryptoRailPanel;
